import { Popup } from 'mapbox-gl';
import { App, createApp, DefineComponent, nextTick } from 'vue';

import i18n from '@/vueI18n';

const DEFAULT_POPUP_ANCHOR_ID = 'popup-anchor';

/**
 * Abstract class that will instanciate a Vue component and bind it a Mapbox popup
 */
abstract class MapBoxPopupBuilder {
  private componentInstance: App<Element>;

  private popupInstance: mapboxgl.Popup;

  constructor(
    private map: mapboxgl.Map,
    private definedComponent: DefineComponent,
  ) {}

  /**
   * Creates a Mapbox popup and display it at the given coordinates
   * @param options
   */
  public async create(options: {
    coordinates: mapboxgl.LngLat;
    className: string;
    closeButton?: boolean;
    anchorId?: string;
  }) {
    const anchorID = options.anchorId ?? DEFAULT_POPUP_ANCHOR_ID;
    this.popupInstance = new Popup({
      className: options.className,
      closeButton: false,
    })
      .setLngLat(options.coordinates)
      .setHTML(`<div id="${anchorID}"></div>`)
      .on('close', this.destroy) // Destroy the component instance when the Popup closes
      .addTo(this.map);

    await nextTick(() => {
      this.componentInstance = createApp(this.definedComponent);
      this.componentInstance
        .use(i18n) // I only provided i18n for now but we could inject more dependencies in the future
        .mount(`#${anchorID}`);
    });

    // Show the Popup
    // There is a `visible` class defined in `styles.scss` that targets all mapbox popup
    this.popupInstance.addClassName('visible');
  }

  /**
   * Closes the popup and destroy the binded vue component
   */
  public destroy() {
    this.componentInstance?.unmount();
    this.popupInstance?.remove();
    delete this.componentInstance;
    delete this.popupInstance;
  }
}

export default MapBoxPopupBuilder;
